import { useEffect, useState, type FormEvent } from 'react';
import { type LoginToken } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { SecretReveal, loginLink } from './SecretReveal.js';

const MIN_TOKEN = 8;
const MAX_TOKEN = 64;
const TOKEN_RE = /^[A-Za-z0-9._-]+$/;

function tokenHint(value: string): string | null {
  const t = value.trim();
  if (!t) return null;
  if (/\s/.test(value)) return 'No spaces allowed.';
  if (t.length < MIN_TOKEN) return `At least ${MIN_TOKEN} characters.`;
  if (t.length > MAX_TOKEN) return `At most ${MAX_TOKEN} characters.`;
  if (!TOKEN_RE.test(t)) return 'Letters, numbers, and - _ . only.';
  return null;
}

/** List / create / revoke personal login tokens (used by Account and Users pages). */
export function LoginTokensPanel({
  load,
  create,
  revoke,
  forEmail,
}: {
  load: () => Promise<LoginToken[]>;
  /** Pass a custom token string, or omit to generate a long random `tnl_…` secret. */
  /** `label` is an optional nickname; empty → server default ("Login"). */
  create: (label: string | undefined, token?: string) => Promise<LoginToken & { token: string }>;
  revoke: (id: string) => Promise<unknown>;
  forEmail?: string;
}): JSX.Element {
  const [tokens, setTokens] = useState<LoginToken[] | null>(null);
  const [label, setLabel] = useState('');
  const [customToken, setCustomToken] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idSuffix = forEmail ?? 'self';
  const hint = tokenHint(customToken);

  async function refresh(): Promise<void> {
    try {
      setTokens(await load());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doCreate(useCustom: boolean): Promise<void> {
    if (busy) return;
    const l = label.trim() || undefined;
    const t = customToken.trim();
    if (useCustom) {
      if (!t || hint) return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await create(l, useCustom ? t : undefined);
      setFresh(r.token);
      setLabel('');
      setCustomToken('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    await doCreate(true);
  }

  async function onGenerate(): Promise<void> {
    await doCreate(false);
  }

  async function onRevoke(t: LoginToken): Promise<void> {
    if (!window.confirm(`Revoke the "${t.label}" login token? Anyone using it will no longer be able to sign in with it.`)) return;
    setBusy(true);
    try {
      await revoke(t.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const canCreateCustom = !!customToken.trim() && !hint && !busy;
  const canGenerate = !busy;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {error && <div className="form-error">{error}</div>}
      {fresh && (
        <>
          <SecretReveal
            label={forEmail ? `New login token for ${forEmail}` : 'Your new login token'}
            value={fresh}
            note="Shown once. Paste it on the sign-in page under “Sign in with team token”. Treat it like a password."
          />
          <SecretReveal
            label="One-click sign-in link"
            value={loginLink(fresh)}
            note="Opening this link signs straight into the account. Share it only over a private channel."
            onDismiss={() => setFresh(null)}
          />
        </>
      )}
      <form className="login-tokens-form" onSubmit={(e) => void onCreate(e)}>
        <div className="form-row">
          <label className="form-label" htmlFor={`tokValue${idSuffix}`}>Your token</label>
          <input
            id={`tokValue${idSuffix}`}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={customToken}
            onChange={(e) => setCustomToken(e.target.value)}
            placeholder="e.g. rocketfuel"
            maxLength={MAX_TOKEN}
          />
          <span className="form-help">
            {hint
              ? hint
              : `${MIN_TOKEN}–${MAX_TOKEN} characters · letters, numbers, - _ . · case-sensitive · unique`}
          </span>
        </div>
        <div className="form-row">
          <label className="form-label" htmlFor={`tokLabel${idSuffix}`}>
            Label <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
          </label>
          <input
            id={`tokLabel${idSuffix}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Login"
            maxLength={60}
          />
          <span className="form-help">Just a nickname to tell tokens apart if you make more than one. Leave blank.</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="submit" className="btn" disabled={!canCreateCustom}>
            Create
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={!canGenerate}
            onClick={() => void onGenerate()}
            title="Creates a long random tnl_… token instead"
          >
            Generate random
          </button>
        </div>
      </form>
      {tokens === null ? (
        <div className="form-help"><span className="spinner" /> Loading tokens…</div>
      ) : tokens.length === 0 ? (
        <div className="form-help">No active login tokens.</div>
      ) : (
        <ul className="token-list">
          {tokens.map((t) => (
            <li key={t.id} className="token-item">
              <div>
                <div className="token-item__label">{t.label}</div>
                <div className="token-item__meta">
                  <code>{t.prefix}…</code> · created {formatDate(t.createdAt)} ·{' '}
                  {t.lastUsedAt ? `last used ${formatDate(t.lastUsedAt)}` : 'never used'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn--danger-ghost"
                style={{ padding: '4px 10px' }}
                disabled={busy}
                onClick={() => void onRevoke(t)}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
